import { useQuery } from '@tanstack/react-query';
import { MenuRoute, MenuResponse } from '@/types/menu';

// Función para obtener los datos del menú
const fetchMenuRoutes = async (lang: string = 'es'): Promise<MenuRoute[]> => {
  const response = await fetch(`/api/${lang}/menu`);
  
  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }
  
  const data: MenuResponse = await response.json();
  
  if (!data.success) {
    throw new Error(data.error || 'Error al obtener el menú');
  }
  
  return data.data.routes || [];
};

// Hook principal para obtener todas las rutas del menú
export function useMenuCategories(lang: string = 'es') {
  return useQuery({
    queryKey: ['menu-categories', lang],
    queryFn: () => fetchMenuRoutes(lang),
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener una categoría específica por slug
export function useMenuCategory(categorySlug: string, lang: string = 'es') {
  return useQuery({
    queryKey: ['menu-category', categorySlug, lang],
    queryFn: async () => {
      const routes = await fetchMenuRoutes(lang);
      return routes.find(route => route.slug === categorySlug);
    },
    enabled: !!categorySlug,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener subcategorías de una categoría específica
export function useMenuSubcategories(categorySlug: string, lang: string = 'es') {
  return useQuery({
    queryKey: ['menu-subcategories', categorySlug, lang],
    queryFn: async () => {
      const routes = await fetchMenuRoutes(lang);
      const category = routes.find(route => route.slug === categorySlug);
      return category?.subMenuRoute || [];
    },
    enabled: !!categorySlug,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener todas las categorías principales (nivel 3)
export function useMainCategories(lang: string = 'es') {
  return useQuery({
    queryKey: ['main-categories', lang],
    queryFn: async () => {
      const routes = await fetchMenuRoutes(lang);
      return routes.filter(route => route.parent === 10);
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}
